import React from 'react';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';

interface LottiePlayerProps {
    /** Ruta del JSON bajo /public (ej. '/assets/lottie/loading.json') */
    src: string;
    /** Ancho/alto en px del recuadro que ocupa la animación */
    size: number;
    className?: string;
    /** Texto para lectores de pantalla. Si se omite, la animación es decorativa. */
    label?: string;
    /** Se muestra solo si el archivo NO carga. Nunca por preferencia de movimiento. */
    fallback?: React.ReactNode;
    /**
     * Tramo de fotogramas a repetir, `[desde, hasta]`.
     *
     * Muchas animaciones traen entrada y salida: repetir el archivo completo
     * deja segundos en los que el sujeto todavía no aparece o ya se fue, y en
     * un elemento permanente eso se lee como que algo se rompió.
     */
    segment?: [number, number];
    /**
     * Qué hacer cuando el sistema pide reducir movimiento.
     *
     * - `functional` (indicadores de carga): SIGUE animando. El movimiento no es
     *   adorno, es lo que comunica "estoy trabajando"; congelarlo haría creer
     *   que la app se trabó. Las pautas de accesibilidad piden menos movimiento,
     *   no cero, y conservar lo que ayuda a entender.
     * - `decorative` (por defecto): se congela en un fotograma representativo.
     *   El dibujo SIGUE VISIBLE — antes desaparecía por completo, que era peor
     *   que animar: dejaba un hueco vacío donde debía haber un botón.
     */
    motion?: 'functional' | 'decorative';
    /**
     * Si se repite en bucle. Por defecto sí.
     *
     * Ponlo en `false` cuando el archivo sea una ENTRADA y no un ciclo: se toca
     * una vez y se queda en el último fotograma. Repetir una entrada deja al
     * sujeto saliendo y volviendo a entrar cada pocos segundos, que en un
     * elemento permanente se lee como que algo falla.
     */
    repetir?: boolean;
    /**
     * Vuelve a tocar el tramo cada N milisegundos, con pausa en medio.
     *
     * Para archivos que son una ENTRADA y no un ciclo. Repetirlos de corrido
     * deja al sujeto entrando y saliendo sin descanso; no repetirlos los deja
     * congelados tras la primera vez. Con una pausa en medio, la animación se
     * vuelve a ver cada tanto sin convertirse en ruido permanente.
     */
    repetirCada?: number;
    /**
     * Tramo que se usa en las repeticiones, si debe ser más corto que el de la
     * primera vez.
     *
     * Sirve cuando el archivo empieza con una entrada que solo tiene sentido
     * una vez (algo que aparece, se despliega, se acomoda) pero contiene dentro
     * un gesto que sí se puede repetir. Repitiendo solo ese gesto no hay salto:
     * el dibujo ya está donde estaba.
     */
    segmentoAlRepetir?: [number, number];
}

/** Respeta la preferencia del sistema de reducir movimiento, en vivo. */
function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = React.useState(
        () => typeof window !== 'undefined'
            && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    );
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq) return;
        const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return reduced;
}

/**
 * LottiePlayer — Sistema 2.0.
 *
 * Carga el JSON bajo demanda desde /public en vez de empotrarlo en el bundle:
 * el del asistente pesa ~100 KB y no debe retrasar el arranque de la app.
 * El dibujo se muestra SIEMPRE; lo único que cambia con la preferencia de
 * movimiento es si se mueve o se queda quieto.
 */
export const LottiePlayer: React.FC<LottiePlayerProps> = ({
    src, size, className = '', label, fallback = null, segment, motion = 'decorative',
    repetir = true, repetirCada, segmentoAlRepetir,
}) => {
    const [data, setData] = React.useState<unknown>(null);
    const [failed, setFailed] = React.useState(false);
    const reduced = usePrefersReducedMotion();
    const ref = React.useRef<LottieRefCurrentProps | null>(null);

    // Congelar solo lo decorativo; un indicador de carga debe seguir girando.
    const freeze = reduced && motion === 'decorative';

    React.useEffect(() => {
        let cancelled = false;
        fetch(src)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
            .then(json => { if (!cancelled) setData(json); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [src]);

    /* Vuelve a tocar el tramo tras la pausa. El temporizador se limpia al
       desmontar y también si el sistema pide reducir movimiento: ahí la
       animación no debe reaparecer sola cada tantos segundos. */
    const [desde, hasta] = segmentoAlRepetir ?? segment ?? [];
    React.useEffect(() => {
        if (!repetirCada || freeze || !data) return;
        const id = window.setInterval(() => {
            const anim = ref.current;
            if (!anim) return;
            if (desde != null && hasta != null) anim.playSegments([[desde, hasta]], true);
            else anim.goToAndPlay(0, true);
        }, repetirCada);
        return () => window.clearInterval(id);
        // `desde`/`hasta` y no el arreglo: se crea nuevo en cada render y
        // reiniciaría el temporizador sin parar.
    }, [repetirCada, freeze, data, desde, hasta]);

    const a11y = label
        ? { role: 'img' as const, 'aria-label': label }
        : { 'aria-hidden': true as const };

    // Solo se cae al fallback si el archivo no está — nunca por preferencias.
    if (failed || !data) {
        return (
            <div style={{ width: size, height: size }} className={`grid place-items-center ${className}`} {...a11y}>
                {fallback}
            </div>
        );
    }

    return (
        <div style={{ width: size, height: size }} className={className} {...a11y}>
            <Lottie
                lottieRef={ref}
                animationData={data}
                loop={repetir && !freeze}
                autoplay={!freeze}
                initialSegment={segment}
                onDOMLoaded={() => {
                    // Fotograma representativo: el punto medio del tramo vivo,
                    // donde el sujeto ya está completamente en escena.
                    if (!freeze) return;
                    const [a, b] = segment ?? [0, 0];
                    ref.current?.goToAndStop(segment ? Math.round((a + b) / 2) : 0, true);
                }}
                style={{ width: '100%', height: '100%' }}
            />
        </div>
    );
};

export default LottiePlayer;
